import { transparentize } from "polished";
import styled, { css, keyframes } from "styled-components";

import { palette } from "_/style";

// Three shades darker than the dark theme's `border.contrast`.
const OUTLINE_COLOR = palette.nickelGrey;

// Every cell navigates to the project, so the whole row reads as clickable.
export const clickableCell = css`
  cursor: pointer;
`;

// Rendered into `document.body` at fixed viewport coordinates, so it floats
// above the table without affecting its layout or being clipped by its
// scroll container. Pointer events pass through to the original thumbnail,
// so moving off it still dismisses the enlargement and row clicks still work.
//
// It is anchored at the original thumbnail's left-centre point. At
// `$thumbnailScale` it exactly covers the original, so it grows out of the
// thumbnail and, while `$isShrinking`, shrinks back into it. Shrinking holds
// its final frame (`forwards`) until the caller removes it on `animationend`.
//
// The frame is an outline rather than a border so it adds nothing to the box
// size, which keeps the start and end frames aligned with the original. It
// fades in while growing and out while shrinking.
export const EnlargedThumbnail = styled.div<{
  $thumbnailScale: number;
  $isShrinking: boolean;
}>`
  position: fixed;
  z-index: 9999;
  transform: translateY(-50%);
  transform-origin: left center;
  pointer-events: none;
  background-color: ${(p) => p.theme.background.secondary};
  border-radius: 4px;
  outline: 1px solid ${OUTLINE_COLOR};
  box-shadow: 0 2px 12px ${(p) => transparentize(0.6, p.theme.tooltip.shadow)};
  animation: ${(p) =>
    p.$isShrinking
      ? css`
          ${shrinkTo(p.$thumbnailScale)} 120ms ease-in
            forwards
        `
      : css`
          ${growFrom(p.$thumbnailScale)} 200ms ease-out
        `};

  /*
    Keep a near-instant animation rather than none, because the caller waits
    for the shrink's \`animationend\` before removing this element.
  */
  @media (prefers-reduced-motion: reduce) {
    animation-duration: 1ms;
  }
`;

const growFrom = (thumbnailScale: number) => keyframes`
  from {
    transform: translateY(-50%) scale(${thumbnailScale});
    outline-color: transparent;
  }
  to {
    transform: translateY(-50%) scale(1);
    outline-color: ${OUTLINE_COLOR};
  }
`;

const shrinkTo = (thumbnailScale: number) => keyframes`
  from {
    transform: translateY(-50%) scale(1);
    outline-color: ${OUTLINE_COLOR};
  }
  to {
    transform: translateY(-50%) scale(${thumbnailScale});
    outline-color: transparent;
  }
`;

// Lets long names, including unbroken ones like file names, wrap instead of
// widening the column on narrow screens.
export const ProjectName = styled.span`
  word-break: break-word;
`;

export const OwnerCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;
